import moment from 'moment';
import React, { useState } from 'react';
import { InterpretDayPassType, InterpretPaymentStatus, InterpretVerificationStatus } from '../../../utils/ui-interpreters';
import { Alert, Button, Chip, Input, Select, Option } from '@material-tailwind/react';
import { AlertCircleIcon, BanknoteIcon, CalendarDaysIcon, CheckCircleIcon, ClockIcon, EyeIcon, PlusIcon, RefreshCwIcon, UserCheckIcon, UserXIcon, XIcon } from 'lucide-react';
import { useGetDayPassesByCustomerId, useApproveDayPass, useCheckInDayPass, useCheckOutDayPass, useUpdateDayPassPayment, useCancelDayPass, useExtendDayPass } from '../../../custom-hooks/use-daypasses';
import { Spinner } from '@material-tailwind/react';
import { DAY_PASSES_TYPES, PAYMENT_METHODS, PAYMENT_METHODS_ENUM, PAYMENT_STATUS } from '../../../utils/enums';
import { toMoney } from '../../../utils/finance';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import DayPassFormModal from '../../daypass/daypassFormModal';

const INITIAL_DAYPASS_FORM_DATA = {
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCNIC: '',
    userId: '',
    seatId: '',
    officeId: '',
    passType: DAY_PASSES_TYPES.HOURLY,
    visitDate: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    purpose: '',
    specialRequirements: '',
    equipmentRequested: []
};

const DayPasses = ({ customer }) => {
    const [isDayPassFormModalOpen, setIsDayPassFormModalOpen] = useState(false);
    const [dayPassDataForModal, setDayPassDataForModal] = useState(INITIAL_DAYPASS_FORM_DATA);
    const { confirm, ConfirmationDialog } = useConfirm();

    const approveDayPassMutation = useApproveDayPass();
    const checkInDayPassMutation = useCheckInDayPass();
    const checkOutDayPassMutation = useCheckOutDayPass();
    const updateDayPassPaymentMutation = useUpdateDayPassPayment();
    const cancelDayPassMutation = useCancelDayPass();
    const extendDayPassMutation = useExtendDayPass();

    // DayPass related hooks
    const {
        data: dayPasses,
        isLoading: isDayPassesLoading,
        error: dayPassesError,
        refetch: refetchDayPasses,
    } = useGetDayPassesByCustomerId(customer._id);

    // Enhanced helper functions for day pass status checks
    const canApprove = (dayPass) => {
        return dayPass.status === 'pending' && !dayPass.isCancelled;
    };

    const canCheckIn = (dayPass) => {
        return dayPass.status === 'approved' &&
            !dayPass.isCheckedIn &&
            !dayPass.isCheckedOut &&
            !dayPass.isCancelled &&
            new Date() >= new Date(dayPass.visitDate);
    };

    const canCheckOut = (dayPass) => {
        return dayPass.isCheckedIn &&
            !dayPass.isCheckedOut &&
            !dayPass.isCancelled;
    };

    const canCancel = (dayPass) => {
        return !dayPass.isCancelled &&
            !dayPass.isCheckedOut &&
            dayPass.status !== 'completed';
    };

    const canExtend = (dayPass) => {
        return dayPass.isCheckedIn &&
            !dayPass.isCheckedOut &&
            !dayPass.isCancelled &&
            dayPass.status === 'approved';
    };

    const canUpdatePayment = (dayPass) => {
        return dayPass.paymentStatus === 'pending' &&
            !dayPass.isCancelled;
    };

    const formatDateTime = (date) => {
        return moment(date).format('MMM DD, h:mm A');
    };

    const formatTime = (date) => {
        return moment(date).format('h:mm A');
    };

    function formatCurrency(amount) {
        return new Intl.NumberFormat('en-PK', {
            style: 'currency',
            currency: 'PKR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    }

    // DayPass handlers
    const handleCreateDayPassOpen = () => {
        setDayPassDataForModal({
            ...INITIAL_DAYPASS_FORM_DATA,
            customerName: customer.name,
            customerEmail: customer.email,
            customerPhone: customer.phone,
            customerCNIC: customer.cnic,
            userId: customer._id
        });
        setIsDayPassFormModalOpen(true);
    };

    const handleDayPassFormModalClose = () => {
        setDayPassDataForModal(INITIAL_DAYPASS_FORM_DATA);
        setIsDayPassFormModalOpen(false);
    };

    const handleDayPassAction = async (action, dayPassId, additionalData = {}) => {
        try {
            switch (action) {
                case 'approve':
                    const approvalConfirmation = await confirm({
                        title: "Approve Day Pass",
                        message: "Are you sure you want to approve this day pass?",
                        confirmText: "Approve",
                        cancelText: "Cancel",
                        confirmBtnColor: "green",
                    });

                    if (approvalConfirmation) {
                        await approveDayPassMutation.mutateAsync({ dayPassId: dayPassId, data: additionalData });
                    }
                    break;
                case 'checkin':
                    const checkInConfirmation = await confirm({
                        title: "Check In Day Pass",
                        message: "Are you sure you want to check in this day pass?",
                        confirmText: "Check In",
                        cancelText: "Cancel",
                        confirmBtnColor: "blue",
                    });
                    if (checkInConfirmation) {
                        await checkInDayPassMutation.mutateAsync({ dayPassId: dayPassId, data: additionalData });
                    }
                    break;
                case 'checkout':
                    const checkOutConfirmation = await confirm({
                        title: "Check Out Day Pass",
                        message: "Are you sure you want to check out this day pass?",
                        confirmText: "Check Out",
                        cancelText: "Cancel",
                        confirmBtnColor: "amber",
                    });
                    if (checkOutConfirmation) {
                        await checkOutDayPassMutation.mutateAsync({ dayPassId: dayPassId, data: additionalData });
                    }
                    break;
                case 'payment':
                    let paymentAmount = 0;
                    let paymentMethod = PAYMENT_METHODS.CASH;
                    let paymentReference = '';

                    const paymentConfirmation = await confirm({
                        title: "Update Day Pass Payment",
                        message: (
                            <div className="space-y-3">
                                <Input
                                    label="Paid Amount"
                                    type="number"
                                    onChange={(e) => paymentAmount = parseFloat(e.target.value) || 0}
                                />
                                <Select
                                    label="Payment Method"
                                    onChange={(value) => paymentMethod = value}
                                    defaultValue={PAYMENT_METHODS.CASH}
                                >
                                    {
                                        PAYMENT_METHODS_ENUM.map((method) => (
                                            <Option value={method.value} key={method.key}>{method.value}</Option>
                                        ))
                                    }
                                </Select>
                                <Input
                                    label="Payment Reference (Optional)"
                                    type="text"
                                    onChange={(e) => paymentReference = e.target.value}
                                />
                            </div>
                        ),
                        confirmText: "Update Payment",
                        cancelText: "Cancel",
                        confirmBtnColor: "blue",
                    });

                    if (paymentConfirmation) {
                        await updateDayPassPaymentMutation.mutateAsync({
                            dayPassId: dayPassId,
                            data: {
                                paymentStatus: PAYMENT_STATUS.PAID,
                                paymentMethod,
                                paymentReference,
                                ...(paymentAmount && { amount: paymentAmount })
                            },
                        });
                    }
                    break;
                case 'cancel':
                    let cancellationReason = '';
                    let refundAmount = 0;

                    const cancelConfirmation = await confirm({
                        title: "Cancel Day Pass",
                        message: (
                            <div className="space-y-3">
                                <textarea
                                    placeholder="Cancellation reason..."
                                    className="w-full p-2 border rounded"
                                    rows="3"
                                    onChange={(e) => cancellationReason = e.target.value}
                                />
                                <Input
                                    label="Refund Amount (Optional)"
                                    type="number"
                                    onChange={(e) => refundAmount = parseFloat(e.target.value) || 0}
                                />
                            </div>
                        ),
                        confirmText: "Cancel Day Pass",
                        cancelText: "Keep Day Pass",
                        confirmBtnColor: "red",
                    });

                    if (cancelConfirmation) {
                        await cancelDayPassMutation.mutateAsync({
                            dayPassId: dayPassId,
                            data: {
                                cancellationReason,
                                refundAmount
                            },
                        });
                    }
                    break;

                case 'extend':
                    let newEndTime = '';
                    let extensionFee = 0;

                    const extendConfirmation = await confirm({
                        title: "Extend Day Pass",
                        message: (
                            <div className="space-y-3">
                                <Input
                                    label="New End Time"
                                    type="datetime-local"
                                    onChange={(e) => newEndTime = e.target.value}
                                />
                                <Input
                                    label="Extension Fee"
                                    type="number"
                                    onChange={(e) => extensionFee = parseFloat(e.target.value) || 0}
                                />
                            </div>
                        ),
                        confirmText: "Extend Day Pass",
                        cancelText: "Cancel",
                        confirmBtnColor: "green",
                    });

                    if (extendConfirmation && newEndTime) {
                        await extendDayPassMutation.mutateAsync({
                            dayPassId: dayPassId,
                            data: {
                                newEndTime: new Date(newEndTime).toISOString(),
                                extensionFee
                            },
                        });
                    }
                    break;
                default:
                    break;
            }
            refetchDayPasses();
        } catch (error) {
            console.error(`Error performing ${action}:`, error);
        }
    };

    // Enhanced Day Pass Card component
    const DayPassCard = ({ dayPass, index, onAction }) => {
        const isExpired = new Date() > new Date(dayPass.endTime);
        const isUpcoming = new Date() < new Date(dayPass.visitDate);

        return (
            <div key={dayPass._id} className={`border rounded-lg p-4 bg-white shadow-sm ${dayPass.isCancelled ? 'border-red-200 bg-red-50' :
                dayPass.isCheckedOut ? 'border-blue-200 bg-blue-50' :
                    dayPass.isCheckedIn ? 'border-green-200 bg-green-50' :
                        isExpired ? 'border-gray-200 bg-gray-50' :
                            'border-purple-200 bg-purple-50'
                }`}>
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h3 className="text-md font-semibold flex items-center gap-2">
                            Day Pass #{index + 1}
                            {InterpretDayPassType(dayPass.passType)}
                        </h3>
                        <p className="text-xs text-gray-600">ID: {dayPass._id}</p>
                        {dayPass.accessCode && (
                            <p className="text-xs text-blue-600 font-mono">
                                Access Code: {dayPass.accessCode}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {InterpretVerificationStatus(dayPass.status)}
                        {InterpretPaymentStatus(dayPass.paymentStatus)}
                        {isUpcoming && <Chip color="orange" size="sm" variant="ghost" value="Upcoming" className="rounded-full" />}
                        {isExpired && !dayPass.isCheckedOut && <Chip color="red" size="sm" variant="ghost" value="Expired" className="rounded-full" />}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    <div>
                        <p className="text-sm text-gray-600">Visit Date</p>
                        <p className="font-medium">{moment(dayPass.visitDate).format("MMM DD, YYYY")}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Time Slot</p>
                        <p className="font-medium">
                            {formatTime(dayPass.startTime)} - {formatTime(dayPass.endTime)}
                        </p>
                        {dayPass.durationHours && (
                            <p className="text-xs text-gray-500">{dayPass.durationHours}h duration</p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Amount</p>
                        <p className="font-medium">{formatCurrency(dayPass.totalAmount)}</p>
                        {dayPass.extensionFee > 0 && (
                            <p className="text-xs text-orange-600">+{formatCurrency(dayPass.extensionFee)} extension</p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Space</p>
                        <p className="font-medium">
                            {dayPass.office ?
                                `Office ${dayPass.office.number}` :
                                dayPass.seat ?
                                    `Seat ${dayPass.seat.seatCode}` :
                                    'Not assigned'
                            }
                        </p>
                    </div>
                </div>

                {dayPass.purpose && (
                    <div className="mb-3">
                        <p className="text-sm text-gray-600">Purpose</p>
                        <p className="text-sm">{dayPass.purpose}</p>
                    </div>
                )}

                {dayPass.specialRequirements && (
                    <div className="mb-3">
                        <p className="text-sm text-gray-600">Special Requirements</p>
                        <p className="text-sm">{dayPass.specialRequirements}</p>
                    </div>
                )}

                {dayPass.equipmentRequested && dayPass.equipmentRequested.length > 0 && (
                    <div className="mb-3">
                        <p className="text-sm text-gray-600">Equipment Requested</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {dayPass.equipmentRequested.map((equipment, idx) => (
                                <Chip key={idx} size="sm" variant="ghost" value={equipment} className="rounded-full" />
                            ))}
                        </div>
                    </div>
                )}

                {/* Check-in/out status */}
                <div className="mb-3">
                    <p className="text-sm text-gray-600">Status Timeline</p>
                    <div className="flex items-center gap-2 text-sm">
                        {dayPass.isCheckedOut ? (
                            <span className="text-blue-600 flex items-center gap-1">
                                <CheckCircleIcon size={12} />
                                Checked Out at {formatDateTime(dayPass.actualCheckOutTime)}
                            </span>
                        ) : dayPass.isCheckedIn ? (
                            <span className="text-green-600 flex items-center gap-1">
                                <CheckCircleIcon size={12} />
                                Checked In at {formatDateTime(dayPass.actualCheckInTime)}
                            </span>
                        ) : (
                            <span className="text-gray-600">Not Checked In</span>
                        )}
                    </div>
                </div>

                {/* Admin and customer notes */}
                {(dayPass.adminNotes || dayPass.customerNotes) && (
                    <div className="mb-3 space-y-2">
                        {dayPass.adminNotes && (
                            <div>
                                <p className="text-sm text-gray-600">Admin Notes</p>
                                <p className="text-sm bg-yellow-50 p-2 rounded">{dayPass.adminNotes}</p>
                            </div>
                        )}
                        {dayPass.customerNotes && (
                            <div>
                                <p className="text-sm text-gray-600">Customer Notes</p>
                                <p className="text-sm bg-blue-50 p-2 rounded">{dayPass.customerNotes}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outlined" color="blue" className="flex items-center gap-1 rounded-full">
                        <EyeIcon size={12} />
                        View Details
                    </Button>

                    {canApprove(dayPass) && (
                        <Button
                            size="sm"
                            color="green"
                            onClick={() => onAction('approve', dayPass._id)}
                            className="flex items-center gap-1 rounded-full"
                        >
                            <CheckCircleIcon size={12} />
                            Approve
                        </Button>
                    )}

                    {canCheckIn(dayPass) && (
                        <Button
                            size="sm"
                            color="blue"
                            onClick={() => onAction('checkin', dayPass._id)}
                            className="flex items-center gap-1 rounded-full"
                        >
                            <UserCheckIcon size={12} />
                            Check In
                        </Button>
                    )}

                    {canCheckOut(dayPass) && (
                        <Button
                            size="sm"
                            color="amber"
                            onClick={() => onAction('checkout', dayPass._id)}
                            className="flex items-center gap-1 rounded-full"
                        >
                            <UserXIcon size={12} />
                            Check Out
                        </Button>
                    )}

                    {canUpdatePayment(dayPass) && (
                        <Button
                            size="sm"
                            color="green"
                            onClick={() => onAction('payment', dayPass._id)}
                            className="flex items-center gap-1 rounded-full"
                        >
                            <BanknoteIcon size={12} />
                            Payment
                        </Button>
                    )}

                    {canExtend(dayPass) && (
                        <Button
                            size="sm"
                            color="indigo"
                            onClick={() => onAction('extend', dayPass._id)}
                            className="flex items-center gap-1 rounded-full"
                        >
                            <ClockIcon size={12} />
                            Extend
                        </Button>
                    )}

                    {canCancel(dayPass) && (
                        <Button
                            size="sm"
                            color="red"
                            variant="outlined"
                            onClick={() => onAction('cancel', dayPass._id)}
                            className="flex items-center gap-1 rounded-full"
                        >
                            <XIcon size={12} />
                            Cancel
                        </Button>
                    )}
                </div>

                {/* Extended time info */}
                {dayPass.isExtended && dayPass.originalEndTime && (
                    <div className="mt-3 p-2 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                        <p className="text-sm text-orange-800">
                            <span className="font-medium">Extended:</span> Originally until {formatTime(dayPass.originalEndTime)}
                        </p>
                    </div>
                )}

                {/* Cancellation info */}
                {dayPass.isCancelled && (
                    <div className="mt-3 p-2 bg-red-50 rounded-lg border-l-4 border-red-400">
                        <p className="text-sm text-red-800">
                            <span className="font-medium">Cancelled:</span> {formatDateTime(dayPass.cancelledAt)}
                        </p>
                        {dayPass.cancellationReason && (
                            <p className="text-sm text-red-700 mt-1">Reason: {dayPass.cancellationReason}</p>
                        )}
                        {dayPass.refundAmount > 0 && (
                            <p className="text-sm text-red-700 mt-1">Refund: {formatCurrency(dayPass.refundAmount)}</p>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="border rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <CalendarDaysIcon size={16} />
                    Day Passes
                </h2>
                <div className="flex gap-2">
                    <Button
                        className="rounded-full border flex items-center gap-2"
                        size="sm"
                        variant="outlined"
                        onClick={() => refetchDayPasses()}
                    >
                        <RefreshCwIcon size={16} />
                        Refresh
                    </Button>
                    <Button
                        className="rounded-full border flex items-center gap-2"
                        size="sm"
                        variant="gradient"
                        onClick={handleCreateDayPassOpen}
                    >
                        <PlusIcon size={16} />
                        Create Day Pass
                    </Button>
                </div>
            </div>

            {/* Statistics */}
            {dayPasses?.dayPasses?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-sm text-blue-600">Total Passes</p>
                        <p className="text-2xl font-bold text-blue-800">{dayPasses.dayPasses.length}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-sm text-green-600">Completed</p>
                        <p className="text-2xl font-bold text-green-800">
                            {dayPasses.dayPasses.filter(dp => dp.isCheckedOut).length}
                        </p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-3">
                        <p className="text-sm text-yellow-600">Pending</p>
                        <p className="text-2xl font-bold text-yellow-800">
                            {dayPasses.dayPasses.filter(dp => dp.status === 'pending').length}
                        </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                        <p className="text-sm text-purple-600">Total Revenue</p>
                        <p className="text-2xl font-bold text-purple-800">
                            {formatCurrency(
                                dayPasses.dayPasses
                                    .filter(dp => !dp.isCancelled)
                                    .reduce((sum, dp) => sum + toMoney(dp.finalAmount), 0)
                            )}
                        </p>
                    </div>
                </div>
            )}

            {isDayPassesLoading ? (
                <div className="flex justify-center p-8">
                    <Spinner />
                </div>
            ) : dayPassesError ? (
                <div className="text-center py-8 text-red-500">
                    <AlertCircleIcon size={24} className="mx-auto mb-2" />
                    <p>Error loading day passes</p>
                    <Button size="sm" variant="outlined" onClick={() => refetchDayPasses()} className="mt-2">
                        Try Again
                    </Button>
                </div>
            ) : dayPasses?.dayPasses?.length === 0 ? (
                <Alert variant="ghost" color="purple">
                    <div className="flex items-center gap-2">
                        <AlertCircleIcon size={16} className="text-purple-500" />
                        <span>No day passes found for {customer.name}</span>
                        <Button variant="gradient" color="purple" className="rounded-full flex items-center gap-2" size="sm" onClick={handleCreateDayPassOpen}>
                            <PlusIcon size={16} />
                            Create First Day Pass
                        </Button>
                    </div>
                </Alert>
            ) : (
                <div className="space-y-4">
                    {dayPasses?.dayPasses?.map((dayPass, index) => (
                        <DayPassCard
                            key={dayPass._id}
                            dayPass={dayPass}
                            index={index}
                            onAction={handleDayPassAction}
                        />
                    ))}
                </div>
            )}
            {isDayPassFormModalOpen && (
                <DayPassFormModal
                    isOpen={isDayPassFormModalOpen}
                    onClose={handleDayPassFormModalClose}
                    defaultData={dayPassDataForModal}
                    customerId={customer._id}
                    onSuccess={() => {
                        refetchDayPasses();
                    }}
                />
            )}

            <ConfirmationDialog />
        </div>
    );
};

export default DayPasses;